import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, User, Link as LinkIcon, Megaphone, Copy, Download, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout';
import ProfileTab from '@/components/shared/ProfileTab';
import {
  PageHeader,
  SectionCard,
  SummaryStatCard,
  EmptyState,
  LoadingSpinner,
  ReadOnlyValue,
} from '@/components/shared/DashboardUI';
import { apiGet, apiPost, apiPut, apiDelete, apiBlob } from '@/lib/api';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'referral-links', label: 'Referral Links', icon: LinkIcon },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { id: 'my-lawyers', label: 'My Lawyers', icon: Users },
  { id: 'profile', label: 'Profile', icon: User },
];

const TABS = NAV_ITEMS.map((item) => item.id);

const pageTitles = {
  dashboard: 'Dashboard',
  'referral-links': 'Referral Links',
  campaigns: 'Campaigns',
  'my-lawyers': 'My Lawyers',
  profile: 'Profile',
};

// Same set the backend's resolve_period() understands (backend/campaigns.py).
// Unrecognised values fall back to a 30-day report there, so keeping this
// list in sync is a nicety, not a correctness requirement.
const PERIODS = [
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'year_to_date', label: 'Year to date' },
  { value: 'all_time', label: 'All time' },
];

const copyToClipboard = (text) => {
  if (!navigator.clipboard) return;
  navigator.clipboard.writeText(text).then(() => toast.success('Link copied'));
};

const GrowthPartnerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  const tabFromPath = (pathname) => {
    const segment = pathname.split('/')[2] || 'dashboard';
    return TABS.includes(segment) ? segment : 'dashboard';
  };

  const [activeTab, setActiveTab] = useState(() => tabFromPath(location.pathname));

  useEffect(() => {
    setActiveTab(tabFromPath(location.pathname));
  }, [location.pathname]);

  const fetchUser = useCallback(async () => {
    try {
      setUser(await apiGet('/users/me'));
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    navigate(tab === 'dashboard' ? '/growth-partner' : `/growth-partner/${tab}`);
  };

  return (
    <AuthenticatedLayout
      user={user}
      activeTab={activeTab}
      onTabChange={handleNavClick}
      navItems={NAV_ITEMS}
      role="growth_partner"
      pageTitle={pageTitles[activeTab]}
    >
      {activeTab === 'dashboard' && <SummaryTab />}
      {activeTab === 'referral-links' && <ReferralLinksTab />}
      {activeTab === 'campaigns' && <CampaignsTab />}
      {activeTab === 'my-lawyers' && <MyLawyersTab />}
      {activeTab === 'profile' && <ProfileTab user={user} onUpdate={fetchUser} />}
    </AuthenticatedLayout>
  );
};

const SummaryTab = () => {
  const [summary, setSummary] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    apiGet('/growth-partner/summary')
      .then(setSummary)
      .catch((error) => console.error('Failed to fetch summary:', error));
    apiGet('/growth-partner/performance?period=last_30_days')
      .then(setPerf)
      .catch((error) => {
        console.error('Failed to fetch performance:', error);
        setPerf({});
      });
  }, []);

  if (!summary) return <LoadingSpinner />;

  const share = summary.revenue_share_percentage;
  const campaigns = perf?.campaigns || [];
  const def = perf?.default;
  const totalClicks = (def?.clicks || 0) + campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
  const totalBrowsers = (def?.unique_browsers || 0) + campaigns.reduce((sum, c) => sum + (c.unique_browsers || 0), 0);
  const totalSignups = (def?.signups || 0) + campaigns.reduce((sum, c) => sum + (c.signups || 0), 0);
  const overallConversion = totalBrowsers ? Math.round((totalSignups / totalBrowsers) * 1000) / 10 : 0;
  const topCampaign = campaigns.reduce((best, c) => (!best || (c.signups || 0) > (best.signups || 0) ? c : best), null);

  return (
    <div data-testid="gp-summary-tab">
      <PageHeader
        title="Your portfolio"
        subtitle="The lawyers assigned to you, and your current revenue share."
      />
      <div className="mcl-stats-row">
        <SummaryStatCard
          icon={Users}
          label="Total Assigned Lawyers"
          value={summary.total_assigned}
          variant="primary"
        />
        <SummaryStatCard
          icon={Users}
          label="Active Lawyers"
          value={summary.active_lawyers}
          variant="success"
        />
        <SummaryStatCard
          icon={LayoutDashboard}
          label="Current Revenue Share"
          value={share === null || share === undefined ? '—' : `${share}%`}
          variant="purple"
        />
      </div>

      <PageHeader
        title="Referral activity"
        subtitle="Clicks and signups from your default link and campaigns over the last 30 days."
      />
      <div className="mcl-stats-row" data-testid="gp-activity-stats">
        <SummaryStatCard icon={LinkIcon} label="Clicks (30d)" value={totalClicks} variant="info" />
        <SummaryStatCard icon={Users} label="Signups (30d)" value={totalSignups} variant="success" />
        <SummaryStatCard icon={Megaphone} label="Conversion (30d)" value={`${overallConversion}%`} variant="purple" />
        <SummaryStatCard icon={Megaphone} label="Top Campaign" value={topCampaign ? topCampaign.name : '—'} variant="primary" />
      </div>
    </div>
  );
};

/* ================================================================
   QR helpers
   ================================================================
   The <img> and download <a> for a partner's QR cannot carry an
   Authorization header, so they cannot depend on the session_token cookie —
   a partner who signed in with Google never gets one (POST
   /auth/google/callback returns the access_token in the response body only,
   unlike POST /auth/login). apiBlob sends the Bearer token instead and hands
   back a Blob; everything here turns that into an object URL and is careful
   to revoke it, since nothing else ever will. */

// Fetches `path` as a blob QR and holds it as an object URL for as long as
// this hook is mounted with that path. Revokes the previous URL before a new
// fetch replaces it, and revokes on unmount — without this every mount/path
// change leaks one blob URL for the life of the tab.
const useQrObjectUrl = (path) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    apiBlob(path)
      .then((blob) => {
        // The effect already cleaned up (path changed or unmounted) by the
        // time this resolves — don't create a URL nobody is left to revoke.
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((error) => console.error('Failed to fetch QR code:', error));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return url;
};

// On-demand QR download for a table row: fetch, trigger the browser's save
// dialog via a detached anchor, then revoke immediately. No persistent state
// is needed here (unlike the default link's <img>, nothing keeps this URL
// around after the click), which is also why nothing leaks across rows.
const downloadQr = async (path, filename) => {
  try {
    const blob = await apiBlob(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(error.message || 'Failed to download QR code');
  }
};

/* ================================================================
   Referral Links Tab
   ================================================================ */
const ReferralLinksTab = () => {
  const [period, setPeriod] = useState('last_30_days');
  const [perf, setPerf] = useState(null);
  const qrUrl = useQrObjectUrl('/growth-partner/qr');

  const load = useCallback((p) => {
    apiGet(`/growth-partner/performance?period=${p}`)
      .then(setPerf)
      .catch((error) => {
        console.error('Failed to fetch performance:', error);
        setPerf({ default: null, campaigns: [] });
      });
  }, []);

  useEffect(() => { load(period); }, [load, period]);

  if (!perf) return <LoadingSpinner />;

  const def = perf.default;
  const campaigns = perf.campaigns || [];

  return (
    <div data-testid="gp-referral-links-tab">
      <PageHeader
        title="Referral Links"
        subtitle="Your default link and every campaign link, with how each is performing."
        actions={
          <select
            className="mcl-form-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="gp-period-select"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        }
      />

      {def && (
        <SectionCard title="Default referral link">
          <div data-testid="gp-default-link">
            <div className="mcl-info-row">
              <span className="mcl-info-label">URL</span>
              <span className="mcl-info-value">{def.url}</span>
            </div>
            <div className="mcl-info-row">
              <span className="mcl-info-label">Code</span>
              <span className="mcl-info-value">{def.referral_code}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="mcl-btn mcl-btn-outline-primary"
                onClick={() => copyToClipboard(def.url)}
                data-testid="gp-copy-default-link"
              >
                <Copy size={16} /> Copy link
              </button>
              <a
                className="mcl-btn mcl-btn-outline"
                href={qrUrl || undefined}
                aria-disabled={!qrUrl}
                download="referral-qr.svg"
                data-testid="gp-download-default-qr"
              >
                <Download size={16} /> Download QR
              </a>
            </div>
            {qrUrl && (
              <img
                src={qrUrl}
                alt="QR code for your default referral link"
                width={120}
                height={120}
                style={{ marginTop: '0.85rem' }}
                data-testid="gp-default-qr-image"
              />
            )}
          </div>
        </SectionCard>
      )}

      <SectionCard title="All links" flush>
        {campaigns.length === 0 ? (
          <EmptyState
            icon={LinkIcon}
            title="No campaigns yet"
            text="Create a campaign from the Campaigns tab to get a trackable link for a specific channel."
          />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table" data-testid="gp-links-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Destination</th>
                  <th>Clicks</th>
                  <th>Unique browsers</th>
                  <th>Signups</th>
                  <th>Conversion</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {def && (
                  <tr data-testid="gp-source-default">
                    <td>Default Referral</td>
                    <td><span className="mcl-badge mcl-badge-success">Active</span></td>
                    <td>/</td>
                    <td>{def.clicks}</td>
                    <td>{def.unique_browsers}</td>
                    <td>{def.signups}</td>
                    <td>{def.conversion_rate}%</td>
                    <td>—</td>
                  </tr>
                )}
                {campaigns.map((c) => (
                  <tr key={c.campaign_code} data-testid={`gp-source-${c.campaign_code}`}>
                    <td>{c.name}</td>
                    <td>
                      {c.active
                        ? <span className="mcl-badge mcl-badge-success">Active</span>
                        : <span className="mcl-badge mcl-badge-secondary">Inactive</span>}
                    </td>
                    <td>{c.destination_path}</td>
                    <td>{c.clicks}</td>
                    <td>{c.unique_browsers}</td>
                    <td>{c.signups}</td>
                    <td>{c.conversion_rate}%</td>
                    <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

/* ================================================================
   Campaigns Tab
   ================================================================ */
const emptyCampaignForm = { name: '', description: '', destination_path: '' };

const CampaignsTab = () => {
  const [data, setData] = useState(null);
  const [perfByCode, setPerfByCode] = useState({});
  const [editing, setEditing] = useState(null); // campaign_code being edited, or null for create
  const [form, setForm] = useState(emptyCampaignForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiGet('/growth-partner/campaigns')
      .then(setData)
      .catch((error) => {
        console.error('Failed to fetch campaigns:', error);
        setData({ campaigns: [], allowed_destinations: [] });
      });
    apiGet('/growth-partner/performance?period=last_30_days')
      .then((perf) => {
        const map = {};
        (perf.campaigns || []).forEach((c) => { map[c.campaign_code] = c; });
        setPerfByCode(map);
      })
      .catch((error) => {
        console.error('Failed to fetch performance:', error);
        setPerfByCode({});
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const destinations = data?.allowed_destinations || [];

  // Give the destination select a real value once its options arrive, so a
  // fresh "New campaign" form never submits an empty destination.
  useEffect(() => {
    if (!editing && destinations.length && !form.destination_path) {
      setForm((f) => ({ ...f, destination_path: destinations[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinations.length, editing]);

  if (!data) return <LoadingSpinner />;

  const campaigns = data.campaigns || [];

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyCampaignForm, destination_path: destinations[0] || '' });
  };

  const openEdit = (c) => {
    setEditing(c.campaign_code);
    setForm({ name: c.name, description: c.description || '', destination_path: c.destination_path });
  };

  const submit = async () => {
    if (!form.name.trim() || !form.destination_path) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        destination_path: form.destination_path,
      };
      if (editing) {
        await apiPut(`/growth-partner/campaigns/${editing}`, payload);
        toast.success('Campaign updated');
      } else {
        await apiPost('/growth-partner/campaigns', payload);
        toast.success('Campaign created');
        setForm({ ...emptyCampaignForm, destination_path: destinations[0] || '' });
      }
      load();
    } catch (error) {
      toast.error(error.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await apiPut(`/growth-partner/campaigns/${c.campaign_code}`, { active: !c.active });
      toast.success(c.active ? 'Campaign deactivated' : 'Campaign activated');
      load();
    } catch (error) {
      toast.error(error.message || 'Failed to update campaign');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This only works when the campaign has no activity yet.`)) return;
    try {
      await apiDelete(`/growth-partner/campaigns/${c.campaign_code}`);
      toast.success('Campaign deleted');
      if (editing === c.campaign_code) openCreate();
      load();
    } catch (error) {
      // The backend refuses to delete a campaign with activity (400, readable
      // detail) so it can be deactivated instead — surface that message
      // rather than a generic failure.
      toast.error(error.message || 'Failed to delete campaign');
    }
  };

  return (
    <div data-testid="gp-campaigns-tab">
      <PageHeader
        title="Campaigns"
        subtitle="Trackable links for a specific channel, like a conference or a printed flyer."
      />

      <SectionCard
        title={editing ? 'Edit campaign' : 'New campaign'}
        actions={editing && (
          <button
            type="button"
            className="mcl-btn mcl-btn-sm mcl-btn-outline"
            onClick={openCreate}
            data-testid="gp-campaign-new"
          >
            <Plus size={14} /> New campaign
          </button>
        )}
      >
        <div className="mcl-form-group">
          <label className="mcl-form-label">Name</label>
          <input
            className="mcl-form-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="gp-campaign-name"
          />
        </div>
        <div className="mcl-form-group">
          <label className="mcl-form-label">Description (optional)</label>
          <input
            className="mcl-form-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="gp-campaign-description"
          />
        </div>
        <div className="mcl-form-group">
          <label className="mcl-form-label">Destination</label>
          <select
            className="mcl-form-select"
            value={form.destination_path}
            onChange={(e) => setForm({ ...form, destination_path: e.target.value })}
            data-testid="gp-campaign-destination"
          >
            {destinations.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        {editing && (
          <div className="mcl-form-group">
            <label className="mcl-form-label">Campaign code</label>
            <ReadOnlyValue value={editing} />
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="mcl-btn mcl-btn-success"
            onClick={submit}
            disabled={saving || !form.name.trim() || !form.destination_path}
            data-testid="gp-campaign-save"
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create campaign'}
          </button>
        </div>
      </SectionCard>

      <SectionCard title="Your campaigns" flush>
        {campaigns.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="No campaigns yet"
            text="Create a campaign above to get a trackable link for a specific channel."
          />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table" data-testid="gp-campaigns-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Destination</th>
                  <th>Clicks</th>
                  <th>Unique browsers</th>
                  <th>Signups</th>
                  <th>Conversion</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const m = perfByCode[c.campaign_code] || {};
                  return (
                    <tr key={c.campaign_code} data-testid={`gp-source-${c.campaign_code}`}>
                      <td>{c.name}</td>
                      <td>
                        {c.active
                          ? <span className="mcl-badge mcl-badge-success">Active</span>
                          : <span className="mcl-badge mcl-badge-secondary">Inactive</span>}
                      </td>
                      <td>{c.destination_path}</td>
                      <td>{m.clicks ?? 0}</td>
                      <td>{m.unique_browsers ?? 0}</td>
                      <td>{m.signups ?? 0}</td>
                      <td>{m.conversion_rate ?? 0}%</td>
                      <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="mcl-action-btn"
                            onClick={() => copyToClipboard(c.url)}
                            data-testid={`gp-copy-${c.campaign_code}`}
                          >
                            <Copy size={13} /> Copy
                          </button>
                          <button
                            type="button"
                            className="mcl-action-btn"
                            onClick={() => downloadQr(
                              `/growth-partner/qr?campaign=${c.campaign_code}`,
                              `${c.campaign_code}-qr.svg`
                            )}
                            data-testid={`gp-download-qr-${c.campaign_code}`}
                          >
                            <Download size={13} /> QR
                          </button>
                          <button
                            type="button"
                            className="mcl-action-btn"
                            onClick={() => openEdit(c)}
                            data-testid={`gp-edit-${c.campaign_code}`}
                          >
                            <Pencil size={13} /> Edit
                          </button>
                          <button
                            type="button"
                            className="mcl-action-btn"
                            onClick={() => toggleActive(c)}
                            data-testid={`gp-toggle-${c.campaign_code}`}
                          >
                            {c.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            type="button"
                            className="mcl-action-btn"
                            onClick={() => remove(c)}
                            data-testid={`gp-delete-${c.campaign_code}`}
                            style={{ color: 'var(--mcl-danger)' }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

// The status column answers two questions on one line: what state the account
// is in, and — once that account is settled — whether the lawyer is actually
// sending cases. The second half is what the partner's revenue share turns on,
// and without it two "Approved" rows sat under a tile counting only one of
// them with nothing to explain the gap.
//
// has_cases is null for an account that is not approved yet, and that renders
// as no suffix at all rather than "No cases yet". The backend never asked the
// question for those lawyers, so the row must not imply an answer to it.
const statusLabel = (lawyer) => {
  if (lawyer.has_cases === null || lawyer.has_cases === undefined) return lawyer.status;
  return `${lawyer.status} · ${lawyer.has_cases ? 'Sending cases' : 'No cases yet'}`;
};

const MyLawyersTab = () => {
  const [lawyers, setLawyers] = useState(null);

  useEffect(() => {
    apiGet('/growth-partner/lawyers')
      .then((data) => setLawyers(data.lawyers || []))
      .catch((error) => {
        console.error('Failed to fetch lawyers:', error);
        setLawyers([]);
      });
  }, []);

  if (lawyers === null) return <LoadingSpinner />;

  if (lawyers.length === 0) {
    return (
      <div data-testid="gp-my-lawyers-tab">
        <PageHeader title="My Lawyers" />
        <EmptyState
          icon={Users}
          title="No lawyers assigned yet"
          text="When an administrator assigns lawyers to you, they will appear here."
        />
      </div>
    );
  }

  return (
    <div data-testid="gp-my-lawyers-tab">
      <PageHeader title="My Lawyers" subtitle={`${lawyers.length} assigned to you.`} />
      <SectionCard title="Assigned lawyers" flush>
        <table className="mcl-table" data-testid="gp-lawyers-table">
          <thead>
            <tr>
              <th>Lawyer</th>
              <th>Law Firm</th>
              <th>Date Joined</th>
              <th>Account Status</th>
            </tr>
          </thead>
          <tbody>
            {lawyers.map((lawyer) => (
              <tr key={lawyer.user_id} data-testid={`gp-lawyer-${lawyer.user_id}`}>
                <td>{lawyer.name}</td>
                <td>{lawyer.firm || '—'}</td>
                <td>{lawyer.joined_at ? new Date(lawyer.joined_at).toLocaleDateString() : '—'}</td>
                <td data-testid={`gp-status-${lawyer.user_id}`}>{statusLabel(lawyer)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
};

export default GrowthPartnerDashboard;
